import type {
  SmartNotification,
  NotificationDelivery,
  EngagementPattern,
  TimingPreference,
  AIError,
} from '@coffeeplexity/shared';

export interface NotificationAnalytics {
  userId: string;
  timeRange: 'day' | 'week' | 'month' | 'quarter';
  totalNotifications: number;
  deliveredNotifications: number;
  openedNotifications: number;
  clickedNotifications: number;
  dismissedNotifications: number;
  expiredNotifications: number;
  deliveryRate: number;
  openRate: number;
  clickThroughRate: number;
  dismissalRate: number;
  engagementScore: number;
  averageTimeToOpen: number; // in minutes
  averageTimeToClick: number; // in minutes
  optimalTimeAccuracy: number;
  userSatisfactionScore: number;
  frequencyOptimization: FrequencyOptimization;
  contentPersonalization: ContentPersonalization;
  timingOptimization: TimingOptimization;
  channelPerformance: ChannelPerformance[];
  improvementSuggestions: string[];
  trends: AnalyticsTrend[];
}

export interface FrequencyOptimization {
  currentFrequency: number; // notifications per day
  optimalFrequency: number;
  frequencyScore: number; // 0-1, higher is better
  userFeedback: {
    tooFrequent: number;
    justRight: number;
    tooInfrequent: number;
  };
  adaptationHistory: FrequencyAdaptation[];
  recommendations: {
    action: 'increase' | 'decrease' | 'maintain';
    confidence: number;
    reasoning: string;
  };
}

export interface ContentPersonalization {
  preferredTone: 'casual' | 'professional' | 'enthusiastic';
  preferredLength: 'short' | 'medium' | 'long';
  emojiPreference: number; // 0-1
  topicPreferences: Record<string, number>;
  contentEngagementRates: Record<string, number>;
  personalizationFactors: {
    skillLevel: number;
    learningStyle: string;
    activityPatterns: string[];
    interests: string[];
  };
  abTestResults: ContentABTestResult[];
}

export interface TimingOptimization {
  optimalHours: number[];
  optimalDays: number[];
  timezoneAdjustment: string;
  contextualFactors: {
    workSchedule: string[];
    sleepSchedule: string[];
    activityPatterns: string[];
  };
  timingAccuracy: number;
  adaptationHistory: TimingAdaptation[];
  predictiveModel: {
    confidence: number;
    factors: string[];
    nextOptimalTime: string;
  };
}

export interface ChannelPerformance {
  channel: 'push' | 'email' | 'in_app' | 'sms';
  deliveryRate: number;
  openRate: number;
  clickThroughRate: number;
  engagementScore: number;
  userPreference: number;
  costEffectiveness: number;
  reliability: number;
}

export interface AnalyticsTrend {
  metric: string;
  timePoints: Array<{
    timestamp: string;
    value: number;
  }>;
  trend: 'increasing' | 'decreasing' | 'stable';
  changeRate: number;
  significance: number;
}

export interface FrequencyAdaptation {
  timestamp: string;
  previousFrequency: number;
  newFrequency: number;
  reason: string;
  confidence: number;
  result: {
    engagementChange: number;
    satisfactionChange: number;
    success: boolean;
  };
}

export interface TimingAdaptation {
  timestamp: string;
  previousTiming: string[];
  newTiming: string[];
  reason: string;
  confidence: number;
  result: {
    accuracyImprovement: number;
    engagementChange: number;
    success: boolean;
  };
}

export interface ContentABTestResult {
  testId: string;
  variants: Array<{
    id: string;
    content: {
      tone: string;
      length: string;
      includeEmoji: boolean;
    };
    performance: {
      impressions: number;
      opens: number;
      clicks: number;
      engagementRate: number;
    };
  }>;
  winner: string;
  confidence: number;
  startDate: string;
  endDate: string;
}

export interface NotificationEffectivenessReport {
  userId: string;
  reportPeriod: {
    startDate: string;
    endDate: string;
  };
  overallScore: number;
  categoryScores: {
    timing: number;
    content: number;
    frequency: number;
    relevance: number;
  };
  keyInsights: string[];
  actionableRecommendations: Array<{
    category: string;
    recommendation: string;
    priority: 'high' | 'medium' | 'low';
    expectedImpact: number;
    implementationComplexity: 'easy' | 'medium' | 'hard';
  }>;
  benchmarks: {
    industryAverage: number;
    topPerformers: number;
    userPercentile: number;
  };
  predictedImprovements: Array<{
    metric: string;
    currentValue: number;
    projectedValue: number;
    timeframe: string;
    confidence: number;
  }>;
}

class NotificationAnalyticsService {
  private baseUrl: string;
  private apiKey: string;

  constructor(
    baseUrl: string = '/api/ai/notifications/analytics',
    apiKey: string = ''
  ) {
    this.baseUrl = baseUrl;
    this.apiKey = apiKey;
  }

  /**
   * Get comprehensive notification analytics for a user
   */
  async getNotificationAnalytics(
    userId: string,
    timeRange: 'day' | 'week' | 'month' | 'quarter' = 'week'
  ): Promise<NotificationAnalytics> {
    try {
      const response = await fetch(
        `${this.baseUrl}/${userId}?timeRange=${timeRange}`,
        {
          headers: {
            ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to get notification analytics: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'NOTIFICATION_ANALYTICS_ERROR',
        message: error.message || 'Failed to get notification analytics',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Track notification performance metrics
   */
  async trackNotificationPerformance(
    notificationId: string,
    metrics: {
      deliveredAt?: string;
      openedAt?: string;
      clickedAt?: string;
      dismissedAt?: string;
      expiredAt?: string;
      context: {
        deviceType: string;
        appState: string;
        batteryLevel?: number;
        networkType?: string;
        location?: {
          latitude: number;
          longitude: number;
        };
      };
    }
  ): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
        body: JSON.stringify({
          notificationId,
          ...metrics,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to track notification performance: ${response.statusText}`
        );
      }
    } catch (error: any) {
      const aiError: AIError = {
        code: 'PERFORMANCE_TRACKING_ERROR',
        message: error.message || 'Failed to track notification performance',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Optimize notification timing based on user patterns
   */
  async optimizeNotificationTiming(
    userId: string,
    currentPatterns: EngagementPattern[],
    timingPreferences: TimingPreference[]
  ): Promise<TimingOptimization> {
    try {
      const response = await fetch(`${this.baseUrl}/optimize/timing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
        body: JSON.stringify({
          userId,
          currentPatterns,
          timingPreferences,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to optimize notification timing: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'TIMING_OPTIMIZATION_ERROR',
        message: error.message || 'Failed to optimize notification timing',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Personalize notification content based on engagement data
   */
  async personalizeNotificationContent(
    userId: string,
    engagementHistory: NotificationDelivery[],
    userPreferences: any
  ): Promise<ContentPersonalization> {
    try {
      const response = await fetch(`${this.baseUrl}/personalize/content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
        body: JSON.stringify({
          userId,
          engagementHistory,
          userPreferences,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to personalize notification content: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'CONTENT_PERSONALIZATION_ERROR',
        message: error.message || 'Failed to personalize notification content',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Optimize notification frequency based on user feedback
   */
  async optimizeNotificationFrequency(
    userId: string,
    currentFrequency: number,
    userFeedback: Array<{
      type: 'too_frequent' | 'just_right' | 'too_infrequent';
      timestamp: string;
      context?: any;
    }>,
    engagementMetrics: {
      openRate: number;
      clickThroughRate: number;
      dismissalRate: number;
    }
  ): Promise<FrequencyOptimization> {
    try {
      const response = await fetch(`${this.baseUrl}/optimize/frequency`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
        body: JSON.stringify({
          userId,
          currentFrequency,
          userFeedback,
          engagementMetrics,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to optimize notification frequency: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'FREQUENCY_OPTIMIZATION_ERROR',
        message: error.message || 'Failed to optimize notification frequency',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Generate comprehensive effectiveness report
   */
  async generateEffectivenessReport(
    userId: string,
    startDate: string,
    endDate: string
  ): Promise<NotificationEffectivenessReport> {
    try {
      const response = await fetch(`${this.baseUrl}/report/effectiveness`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
        body: JSON.stringify({
          userId,
          startDate,
          endDate,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to generate effectiveness report: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'EFFECTIVENESS_REPORT_ERROR',
        message: error.message || 'Failed to generate effectiveness report',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Get real-time notification performance dashboard data
   */
  async getPerformanceDashboard(
    userId: string,
    timeRange: 'hour' | 'day' | 'week' = 'day'
  ): Promise<{
    realTimeMetrics: {
      activeNotifications: number;
      pendingDeliveries: number;
      recentEngagements: number;
      currentEngagementRate: number;
    };
    hourlyBreakdown: Array<{
      hour: number;
      delivered: number;
      opened: number;
      clicked: number;
      engagementRate: number;
    }>;
    channelBreakdown: ChannelPerformance[];
    topPerformingContent: Array<{
      contentType: string;
      engagementRate: number;
      volume: number;
    }>;
    alerts: Array<{
      type: 'performance_drop' | 'frequency_issue' | 'timing_suboptimal';
      message: string;
      severity: 'low' | 'medium' | 'high';
      actionRequired: boolean;
    }>;
  }> {
    try {
      const response = await fetch(
        `${this.baseUrl}/dashboard/${userId}?timeRange=${timeRange}`,
        {
          headers: {
            ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to get performance dashboard: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'DASHBOARD_ERROR',
        message: error.message || 'Failed to get performance dashboard',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Run A/B test for notification optimization
   */
  async runNotificationABTest(
    userId: string,
    testConfig: {
      name: string;
      variants: Array<{
        id: string;
        name: string;
        config: {
          timing?: {
            preferredHours: number[];
            frequency: number;
          };
          content?: {
            tone: 'casual' | 'professional' | 'enthusiastic';
            length: 'short' | 'medium' | 'long';
            includeEmoji: boolean;
          };
          frequency?: {
            multiplier: number;
            maxPerDay: number;
          };
        };
      }>;
      duration: number; // days
      successMetric: 'engagement_rate' | 'click_through_rate' | 'satisfaction';
      minimumSampleSize: number;
    }
  ): Promise<{
    testId: string;
    status: 'created' | 'running' | 'completed';
    startDate: string;
    estimatedEndDate: string;
    currentResults?: {
      variants: Array<{
        id: string;
        impressions: number;
        conversions: number;
        conversionRate: number;
        confidence: number;
      }>;
      leadingVariant?: string;
      statisticalSignificance: boolean;
    };
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/ab-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
        body: JSON.stringify({
          userId,
          testConfig,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to run A/B test: ${response.statusText}`);
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'AB_TEST_ERROR',
        message: error.message || 'Failed to run A/B test',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Get A/B test results and recommendations
   */
  async getABTestResults(testId: string): Promise<{
    testId: string;
    status: 'running' | 'completed' | 'stopped';
    results: {
      winner: string;
      confidence: number;
      improvement: number;
      variants: Array<{
        id: string;
        name: string;
        performance: {
          impressions: number;
          conversions: number;
          conversionRate: number;
          engagementRate: number;
        };
        isWinner: boolean;
      }>;
    };
    recommendations: Array<{
      action: string;
      reasoning: string;
      expectedImpact: number;
      implementationSteps: string[];
    }>;
    nextSteps: string[];
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/ab-test/${testId}`, {
        headers: {
          ...(this.apiKey && { Authorization: `Bearer ${this.apiKey}` }),
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to get A/B test results: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error: any) {
      const aiError: AIError = {
        code: 'AB_TEST_RESULTS_ERROR',
        message: error.message || 'Failed to get A/B test results',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      throw aiError;
    }
  }

  /**
   * Calculate notification effectiveness score
   */
  calculateEffectivenessScore(
    deliveryRate: number,
    openRate: number,
    clickThroughRate: number,
    dismissalRate: number,
    timingAccuracy: number,
    userSatisfaction: number
  ): number {
    // Weighted scoring algorithm
    const weights = {
      delivery: 0.15,
      open: 0.25,
      click: 0.2,
      dismissal: -0.15, // negative weight for dismissals
      timing: 0.2,
      satisfaction: 0.35,
    };

    const score =
      deliveryRate * weights.delivery +
      openRate * weights.open +
      clickThroughRate * weights.click +
      (1 - dismissalRate) * Math.abs(weights.dismissal) +
      timingAccuracy * weights.timing +
      userSatisfaction * weights.satisfaction;

    return Math.max(0, Math.min(1, score));
  }

  /**
   * Analyze notification timing patterns
   */
  analyzeTimingPatterns(deliveries: NotificationDelivery[]): {
    bestHours: number[];
    worstHours: number[];
    bestDays: number[];
    worstDays: number[];
    patterns: Array<{
      pattern: string;
      confidence: number;
      description: string;
    }>;
  } {
    if (deliveries.length === 0) {
      return {
        bestHours: [],
        worstHours: [],
        bestDays: [],
        worstDays: [],
        patterns: [],
      };
    }

    // Group by hour and day
    const hourlyStats: Record<number, { total: number; engaged: number }> = {};
    const dailyStats: Record<number, { total: number; engaged: number }> = {};

    deliveries.forEach(delivery => {
      const deliveryTime = new Date(delivery.deliveredAt);
      const hour = deliveryTime.getHours();
      const day = deliveryTime.getDay();
      const isEngaged = !!(delivery.openedAt || delivery.clickedAt);

      // Hour stats
      if (!hourlyStats[hour]) {
        hourlyStats[hour] = { total: 0, engaged: 0 };
      }
      hourlyStats[hour].total++;
      if (isEngaged) {
        hourlyStats[hour].engaged++;
      }

      // Day stats
      if (!dailyStats[day]) {
        dailyStats[day] = { total: 0, engaged: 0 };
      }
      dailyStats[day].total++;
      if (isEngaged) {
        dailyStats[day].engaged++;
      }
    });

    // Calculate engagement rates and sort
    const hourlyRates = Object.entries(hourlyStats)
      .map(([hour, stats]) => ({
        hour: parseInt(hour),
        rate: stats.engaged / stats.total,
        total: stats.total,
      }))
      .filter(item => item.total >= 3); // Minimum sample size

    const dailyRates = Object.entries(dailyStats)
      .map(([day, stats]) => ({
        day: parseInt(day),
        rate: stats.engaged / stats.total,
        total: stats.total,
      }))
      .filter(item => item.total >= 3);

    hourlyRates.sort((a, b) => b.rate - a.rate);
    dailyRates.sort((a, b) => b.rate - a.rate);

    // Identify patterns
    const patterns: Array<{
      pattern: string;
      confidence: number;
      description: string;
    }> = [];

    // Morning vs Evening pattern
    const morningHours = hourlyRates.filter(h => h.hour >= 6 && h.hour <= 11);
    const eveningHours = hourlyRates.filter(h => h.hour >= 18 && h.hour <= 22);

    if (morningHours.length > 0 && eveningHours.length > 0) {
      const morningAvg =
        morningHours.reduce((sum, h) => sum + h.rate, 0) / morningHours.length;
      const eveningAvg =
        eveningHours.reduce((sum, h) => sum + h.rate, 0) / eveningHours.length;

      if (morningAvg > eveningAvg * 1.2) {
        patterns.push({
          pattern: 'morning_preference',
          confidence: Math.min(0.9, (morningAvg - eveningAvg) * 2),
          description:
            'User shows significantly higher engagement in morning hours',
        });
      } else if (eveningAvg > morningAvg * 1.2) {
        patterns.push({
          pattern: 'evening_preference',
          confidence: Math.min(0.9, (eveningAvg - morningAvg) * 2),
          description:
            'User shows significantly higher engagement in evening hours',
        });
      }
    }

    // Weekday vs Weekend pattern
    const weekdays = dailyRates.filter(d => d.day >= 1 && d.day <= 5);
    const weekends = dailyRates.filter(d => d.day === 0 || d.day === 6);

    if (weekdays.length > 0 && weekends.length > 0) {
      const weekdayAvg =
        weekdays.reduce((sum, d) => sum + d.rate, 0) / weekdays.length;
      const weekendAvg =
        weekends.reduce((sum, d) => sum + d.rate, 0) / weekends.length;

      if (weekdayAvg > weekendAvg * 1.15) {
        patterns.push({
          pattern: 'weekday_preference',
          confidence: Math.min(0.9, (weekdayAvg - weekendAvg) * 2),
          description: 'User shows higher engagement on weekdays',
        });
      } else if (weekendAvg > weekdayAvg * 1.15) {
        patterns.push({
          pattern: 'weekend_preference',
          confidence: Math.min(0.9, (weekendAvg - weekdayAvg) * 2),
          description: 'User shows higher engagement on weekends',
        });
      }
    }

    return {
      bestHours: hourlyRates.slice(0, 3).map(item => item.hour),
      worstHours: hourlyRates.slice(-3).map(item => item.hour),
      bestDays: dailyRates.slice(0, 3).map(item => item.day),
      worstDays: dailyRates.slice(-3).map(item => item.day),
      patterns,
    };
  }

  /**
   * Generate improvement suggestions based on analytics
   */
  generateImprovementSuggestions(analytics: NotificationAnalytics): Array<{
    category: string;
    suggestion: string;
    priority: 'high' | 'medium' | 'low';
    expectedImpact: number;
    implementationComplexity: 'easy' | 'medium' | 'hard';
  }> {
    const suggestions: Array<{
      category: string;
      suggestion: string;
      priority: 'high' | 'medium' | 'low';
      expectedImpact: number;
      implementationComplexity: 'easy' | 'medium' | 'hard';
    }> = [];

    // Timing optimization suggestions
    if (analytics.timingOptimization.timingAccuracy < 0.7) {
      suggestions.push({
        category: 'timing',
        suggestion:
          'Optimize notification timing based on user engagement patterns',
        priority: 'high',
        expectedImpact: 0.25,
        implementationComplexity: 'medium',
      });
    }

    // Frequency optimization suggestions
    if (analytics.frequencyOptimization.frequencyScore < 0.6) {
      const userFeedback = analytics.frequencyOptimization.userFeedback;
      if (userFeedback.tooFrequent > userFeedback.tooInfrequent) {
        suggestions.push({
          category: 'frequency',
          suggestion:
            'Reduce notification frequency to improve user satisfaction',
          priority: 'high',
          expectedImpact: 0.2,
          implementationComplexity: 'easy',
        });
      } else if (userFeedback.tooInfrequent > userFeedback.tooFrequent) {
        suggestions.push({
          category: 'frequency',
          suggestion: 'Increase notification frequency to maintain engagement',
          priority: 'medium',
          expectedImpact: 0.15,
          implementationComplexity: 'easy',
        });
      }
    }

    // Content personalization suggestions
    if (analytics.openRate < 0.4) {
      suggestions.push({
        category: 'content',
        suggestion:
          'Personalize notification content based on user preferences',
        priority: 'high',
        expectedImpact: 0.3,
        implementationComplexity: 'medium',
      });
    }

    // Channel optimization suggestions
    const bestChannel = analytics.channelPerformance.reduce((best, current) =>
      current.engagementScore > best.engagementScore ? current : best
    );

    if (bestChannel.engagementScore > 0.6) {
      const otherChannels = analytics.channelPerformance.filter(
        c => c.channel !== bestChannel.channel && c.engagementScore < 0.4
      );

      if (otherChannels.length > 0) {
        suggestions.push({
          category: 'channel',
          suggestion: `Focus more on ${bestChannel.channel} notifications and reduce ${otherChannels.map(c => c.channel).join(', ')}`,
          priority: 'medium',
          expectedImpact: 0.18,
          implementationComplexity: 'easy',
        });
      }
    }

    // Engagement rate suggestions
    if (analytics.clickThroughRate < 0.1) {
      suggestions.push({
        category: 'engagement',
        suggestion:
          'Improve call-to-action clarity and relevance in notifications',
        priority: 'high',
        expectedImpact: 0.35,
        implementationComplexity: 'medium',
      });
    }

    return suggestions.sort((a, b) => {
      const priorityWeight = { high: 3, medium: 2, low: 1 };
      return (
        priorityWeight[b.priority] - priorityWeight[a.priority] ||
        b.expectedImpact - a.expectedImpact
      );
    });
  }
}

export const notificationAnalyticsService = new NotificationAnalyticsService();
